<!-- The sticky rail at the bottom of a screen whose point is one action.
     Factory-owned.

     WHY IT IS HERE RATHER THAN IN EACH APP. It was `<div class="actions">`
     written out by hand on eight screens of one app, and on three of them it
     hid something real: a total under the rail on a review screen, a
     description cut mid-sentence, a task list's third row behind the rail and
     the undo bar together. The cause is structural rather than a wrong number —
     a rail in flow at the end of the content leaves the last screenful of a
     long page nowhere to go.

     THE RAIL CARRIES ONE ACTION. That is a design rule, not a measurement. A
     rail holding a filled primary button AND a secondary link measured 171px on
     a 772px scroll box: a fifth of the screen permanently spoken for. Secondary
     actions belong in the content, above it.

     It measures itself for the one thing measurement is genuinely needed for —
     `scroll-padding-bottom`. See `apply()` for why the obvious version of that
     (padding the scroll box) is wrong. -->
<template>
  <div ref="el" class="action-rail">
    <slot />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const el = ref(null);

let content = null;
let observer = null;
let scrollEl = null;

function apply() {
  if (!el.value || !content) return;
  const height = Math.ceil(el.value.getBoundingClientRect().height);
  content.style.setProperty('--rail-height', `${height}px`);

  // SCROLL padding, not box padding. Padding the scroll box is the obvious move
  // and it is wrong: a `bottom: 0` sticky element pins to the scrollport's
  // CONTENT edge, so the padding lifts the rail off the bottom of the screen by
  // exactly its own height — measured at 203px, which put the rail in the
  // middle of the page with the content running underneath it.
  //
  // `scroll-padding-bottom` moves no pixels. It tells the browser where the
  // usable bottom of the scrollport is, so `scrollIntoView` and — the case that
  // matters on a form — focusing a field near the end brings it to rest ABOVE
  // the rail rather than behind it.
  if (scrollEl) scrollEl.style.scrollPaddingBottom = `${height}px`;
}

onMounted(async () => {
  const page = el.value?.closest?.('.ion-page');
  content = page?.querySelector?.('ion-content') ?? null;
  if (!content) return;

  // Tells the page's own CSS that this screen's bottom strip is already spoken
  // for, so nothing adds the toast clearance on top of the rail.
  content.classList.add('has-rail');

  if (content.getScrollElement) scrollEl = await content.getScrollElement();
  apply();

  // The rail's height is not a constant: it changes with the copy inside the
  // button, with a note appearing under it, and most of all with the reading
  // size. At 200% it is more than twice as tall as at 100%.
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(apply);
    observer.observe(el.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
  if (scrollEl) scrollEl.style.removeProperty('scroll-padding-bottom');
  scrollEl = null;
  content?.classList.remove('has-rail');
  content?.style.removeProperty('--rail-height');
  content = null;
});
</script>
